// ConfirmationPage.js - Complete File (Full Page Layout)
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../../../assets/css/patient/ConfirmBooking.css';

// ==================== HELPERS ====================

const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
};

const getPaymentMethodLabel = (method) => {
  const map = {
    card:      '💳 Credit / Debit Card',
    easypaisa: '📱 Easypaisa',
    jazzcash:  '💚 JazzCash',
    bank:      '🏦 Bank Transfer',
  };
  return map[method] || method;
};

const handlePrint = () => window.print();

const handleShare = (booking) => {
  const text = `Appointment Confirmed!\nBooking ID: ${booking.bookingId}\nDoctor: ${booking.doctor?.name}\nDate: ${formatDate(booking.date)}\nTime: ${booking.time}`;
  if (navigator.share) {
    navigator.share({ title: 'Appointment Confirmation', text });
  } else {
    navigator.clipboard.writeText(text).then(() => alert('Details copied to clipboard!'));
  }
};

// ==================== COMPONENT ====================

const ConfirmationPage = () => {
  const location = useLocation();   // ✅ React Router se state lo
  const navigate  = useNavigate();  // ✅ Back ke liye navigate

  // ✅ BookingPage ne navigate('/confirm-booking', { state: { booking } }) kiya tha
  const booking = location.state?.booking;

  // Guard — agar koi seedha URL pe aaye bina booking ke
  if (!booking || !booking.bookingId) {
    return (
      <div style={{ textAlign: 'center', padding: '4rem', fontFamily: 'sans-serif' }}>
        <h2>⚠️ No Booking Found</h2>
        <p>Please complete the booking process first.</p>
        <button
          onClick={() => navigate('/')}
          style={{ marginTop: '1rem', padding: '0.75rem 2rem', background: '#00005a', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '1rem' }}
        >
          ← Go to Home
        </button>
      </div>
    );
  }

  return (
    <div className="cp-page">
      <div className="cp-container">

        {/* Success Header */}
        <div className="cp-header">
          <div className="cp-success-ring">
            <span className="cp-success-icon">✅</span>
          </div>
          <h2 className="cp-header-title">Appointment Confirmed!</h2>
          <p className="cp-header-subtitle">Your appointment has been successfully booked.</p>
          <div className="cp-booking-id">🔖 Booking ID: {booking.bookingId}</div>
        </div>

        <div className="cp-body">

          {/* Appointment Details */}
          <div className="cp-appt-card">
            <div className="cp-card-title">📋 Appointment Details</div>
            <div className="cp-doctor-row">
              <img src={booking.doctor?.image} alt={booking.doctor?.name} className="cp-doctor-img" />
              <div>
                <div className="cp-doctor-name">{booking.doctor?.name}</div>
                <div className="cp-doctor-spec">{booking.doctor?.specialty}</div>
                <div style={{ fontSize: '0.78rem', color: '#FF8C00', fontWeight: 700, marginTop: '0.2rem' }}>
                  {booking.doctor?.degree}
                </div>
              </div>
            </div>
            <div className="cp-detail-grid">
              {[
                { icon: '📅', label: 'Date',      val: formatDate(booking.date) },
                { icon: '🕐', label: 'Time',      val: booking.time },
                { icon: '📍', label: 'Mode',      val: 'Online Consultation' },
                { icon: '🗓️', label: 'Booked On', val: booking.bookedAt },
              ].map((item, i) => (
                <div key={i} className="cp-detail-item">
                  <span className="cp-detail-icon">{item.icon}</span>
                  <div>
                    <div className="cp-detail-label">{item.label}</div>
                    <div className="cp-detail-val">{item.val}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Patient Info */}
          <div className="cp-patient-card">
            <div className="cp-card-title">👤 Patient Information</div>
            <div className="cp-patient-grid">
              {[
                { icon: '🧑',  label: 'Name',   val: booking.patient?.name },
                { icon: '⚧',   label: 'Gender', val: booking.patient?.gender, capitalize: true },
                { icon: '📧',  label: 'Email',  val: booking.patient?.email },
                { icon: '📞',  label: 'Phone',  val: booking.patient?.phone },
                { icon: '🎂',  label: 'Age',    val: booking.patient?.age ? `${booking.patient.age} years` : '' },
              ].map((item, i) => (
                <div key={i} className="cp-detail-item">
                  <span className="cp-detail-icon">{item.icon}</span>
                  <div>
                    <div className="cp-detail-label">{item.label}</div>
                    <div className="cp-detail-val" style={item.capitalize ? { textTransform: 'capitalize' } : {}}>
                      {item.val}
                    </div>
                  </div>
                </div>
              ))}
              {booking.patient?.problem && (
                <div className="cp-detail-item" style={{ gridColumn: '1 / -1' }}>
                  <span className="cp-detail-icon">📝</span>
                  <div>
                    <div className="cp-detail-label">Problem</div>
                    <div className="cp-detail-val" style={{ fontWeight: 600, fontSize: '0.87rem' }}>
                      {booking.patient.problem}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Payment */}
          <div className="cp-payment-card">
            <div className="cp-payment-left">
              <div className="cp-card-title">💳 Payment</div>
              <div className="cp-payment-method">{getPaymentMethodLabel(booking.paymentMethod)}</div>
              <div className="cp-payment-status">✅ Payment Confirmed</div>
            </div>
            <div className="cp-fee-display">
              <div className="cp-fee-tag">Amount Paid</div>
              <div className="cp-fee-amt">{booking.fee}</div>
            </div>
          </div>

          {/* Actions */}
          <div className="cp-actions">
            <button className="cp-btn-primary" onClick={handlePrint}>
              🖨️ Print Receipt
            </button>
            <button className="cp-btn-secondary" onClick={() => handleShare(booking)}>
              📤 Share Details
            </button>
            {/* ✅ Back to Home — React Router se navigate karo */}
            <button className="cp-btn-full" onClick={() => navigate('/')}>
              ← Back to Home
            </button>
          </div>

          {/* Quranic Note */}
          <div className="cp-quranic">
            <div className="cp-quranic-arabic">وَإِذَا مَرِضْتُ فَهُوَ يَشْفِينِ</div>
            <div className="cp-quranic-trans">"And when I am ill, it is He who cures me."</div>
            <div className="cp-quranic-ref">— Surah Ash-Shu'ara (26:80)</div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ConfirmationPage;